import { createAsyncThunk } from '@reduxjs/toolkit';

import { WithToken } from '~/api/auth/authApiTypes';
import { bookApi } from '~/api/book/bookApi';
import { Book } from '~/api/book/bookApiTypes';
import { BookTag } from '~/api/book/bookTagApiTypes';
import { Paginated } from '~/api/provider/providerTypes';
import { tagPageActions, tagPageSliceName } from '~/atomic/page/catalog/tagPage/tagPage.slice';
import { createCatalogFilterUrl } from '~/feature/catalog/filter/catalogFilter.data';
import {
  catalogFilterGenresSelector,
  catalogFilterSelector,
} from '~/feature/catalog/filter/catalogFilter.selector';
import { paginationSelector } from '~/feature/pagination/pagination.selector';
import { paginationActions } from '~/feature/pagination/pagination.slice';
import { environments } from '~/lib/const';
import { RootState } from '~/store';

export const getBooks = createAsyncThunk<
Paginated<Book[]> & { popularTags: BookTag[] },
WithToken | void,
{ rejectValue: { error: string }; state: RootState }>(
  `${tagPageSliceName}/getBookList`,
  async (
    data,
    thunkAPI,
  ) => {
    try {
      const {
        tagSlug,
        completeStatus,
        isOnlyFree,
        bookType,
        withAudiobook,
        sortingType,
        isWithAbonnement,
        isCanDownload,
        isExclusive,
        withAdvertisingFromBookriver,
      } = catalogFilterSelector(thunkAPI.getState());
      const selectedGenreSlugs = catalogFilterGenresSelector(thunkAPI.getState());
      const { page, perPage } = paginationSelector(thunkAPI.getState());
      const selectedGenresSlugs = selectedGenreSlugs.join(',').replace(' ', '');

      const result = await bookApi.getBookList({
        additionalGenres: selectedGenresSlugs.length > 0
          ? selectedGenresSlugs
          : undefined,
        statusComplete: completeStatus,
        free: isOnlyFree ? true : undefined,
        download: isCanDownload ? true : undefined,
        exclusive: isExclusive ? true : undefined,
        withAudiobook: withAudiobook ? true : undefined,
        type: bookType,
        withAbonnement: isWithAbonnement ? true : undefined,
        tag: tagSlug === '' ? undefined : tagSlug,
        sortingType,
        hideAudiobooksWithoutEbooks: withAudiobook ? false : undefined,
        withAdvertisingFromBookriver: withAdvertisingFromBookriver ? true : undefined,
        page,
        perPage,
        token: data && data?.token ? data.token : undefined,
      });

      if (result && 'data' in result) {
        const popularTags = result.popularTags.map((tag) => {
          const urlWithTagResult = createCatalogFilterUrl(thunkAPI.getState(), { tagSlug: tag.slug });
          const urlWithoutTagResult = createCatalogFilterUrl(thunkAPI.getState(), { tagSlug: '' });

          return {
            ...tag,
            url: urlWithTagResult,
            withoutUrl: urlWithoutTagResult,
          };
        });

        thunkAPI.dispatch(paginationActions.initPagination({
          total: result.total,
          page: result.currentPage,
          perPage: result.perPage,
        }));
        thunkAPI.dispatch(tagPageActions.changeBooks(result.data));

        return {
          ...result,
          data: result.data,
          popularTags: selectedGenresSlugs.length > 0 ? popularTags : [],
        };
      }
    } catch (error) {
      if (environments.isClient) {
        const { message } = await import('~/atomic/atom/message');
        message.error(error.message);
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

export const getBooksWithLoading = createAsyncThunk<
Paginated<Book[]> & { popularTags: BookTag[] },
void,
{ rejectValue: { error: string }; state: RootState }>(
  `${tagPageSliceName}/getBookList`,
  async (
    _data,
    thunkAPI,
  ) => {
    thunkAPI.dispatch(tagPageActions.setIsLoadBooks(true));
    const result = await thunkAPI.dispatch(getBooks());
    thunkAPI.dispatch(tagPageActions.setIsLoadBooks(false));

    if (getBooks.rejected.match(result)) {
      return thunkAPI.rejectWithValue({ error: result.error.message });
    }
    if (getBooks.fulfilled.match(result)) {
      return result.payload;
    }
  },
);
